import { FormattedMessage, useIntl } from 'react-intl'
import FormatCurrency from 'components/Common/FormatCurrency'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscount from 'LuxKit/PricePoints/Value/PriceRowValueDiscount'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import type { CruiseOfferPriceVariation } from 'lib/cruises/cruiseUtils'
import CruisePriceFromLabel from 'components/Cruises/Common/CruisePriceFromLabel'
import CruisePromotionPriceUnit from 'components/Cruises/Common/CruisePromotionPriceUnit'
import useCruiseCabinTypeLabels from 'hooks/Cruise/useCruiseCabinTypeLabels'

export interface PriceRoomTypeProps {
  offer?: App.CruiseOffer
  priceFrom: App.CruisePriceDetails
  price: CruiseOfferPriceVariation
  memberPrice?: CruiseOfferPriceVariation
  memberPriceType?: App.Cruises.CruiseMemberPriceType
  header?: boolean
  hasValidDepartures?: boolean
}

function CruisePriceRoomTypeInfo({ priceFrom, price, memberPrice, memberPriceType, hasValidDepartures, header = false }: PriceRoomTypeProps) {
  const intl = useIntl()
  const cabinTypeLabels = useCruiseCabinTypeLabels()
  const cabinType = priceFrom.cabinCategory
    ? cabinTypeLabels[priceFrom.cabinCategory.toLowerCase()] ?? priceFrom.cabinCategory
    : undefined
  const promotionalPrice = price.promotionalPrice
  const perPersonPrice = promotionalPrice?.perPersonPrice
  const hasPerPersonPrice = !!perPersonPrice

  return (
    <>
      <Group direction="vertical" gap={4}>
        {hasValidDepartures && <div>
          {promotionalPrice && <>
            <PriceRowPriceCaption>
              <CruisePriceFromLabel promotion={promotionalPrice.promotion} cabinType={cabinType} />
            </PriceRowPriceCaption>
            {hasPerPersonPrice ? <>
              <Caption variant="medium" colour="success-dark">
                <FormattedMessage
                  id="01ZsUhS60i_gK_k4VanJ"
                  defaultMessage="{price}/person"
                  description="Primary per-person price displayed above the total family cruise promotional price"
                  values={{
                    price: <Heading as="span" variant="heading6" colour="success-dark">
                      <FormatCurrency value={perPersonPrice} />
                    </Heading>,
                  }}
                />
              </Caption>
              <Caption variant="medium" colour="light-foreground-alt">
                <FormatCurrency value={promotionalPrice.total} />&nbsp;
                <CruisePromotionPriceUnit promotion={promotionalPrice.promotion} />
              </Caption>
            </> : <LuxPlusPriceStack size="S" price={promotionalPrice.total} saleUnit={promotionalPrice.type} />}
          </>}
          {!promotionalPrice && <>
            <PriceRowPriceCaption><CruisePriceFromLabel /></PriceRowPriceCaption>
            <Group direction="horizontal" gap={4} verticalAlign="end">
              <LuxPlusPriceStack
                size="S"
                price={price.primaryPrice.total}
                memberPrice={memberPrice?.primaryPrice.total}
                saleUnit={price.primaryPrice.type}
                mode={memberPriceType === 'STANDARD_PRICE' ? 'member-only' : undefined}
              />
            </Group>
            <PriceRowCaption>
              {intl.formatMessage({
                id: 'F9_v4gAnxcZCq6ZVxwDc',
                defaultMessage: 'Twin share',
                description: 'Cruise price caption stating the twin-share/double-occupancy basis: two passengers sharing one cabin. This is an occupancy/pricing basis and does not specify or promise two separate beds.',
              })}
            </PriceRowCaption>
          </>}

          {!header && priceFrom.discountPills?.discountPercentage && <PriceRowValueDiscount
            type="estimate"
            discountPercentage={priceFrom.discountPills.discountPercentage}
          />}
        </div>}
      </Group>

      {!hasValidDepartures && <Group direction="vertical" verticalAlign="center" fullWidth>
        <BodyText variant="large" weight="bold" align="center">
          {intl.formatMessage({
            id: 'hZqiWh9QZkxCrRGrIaYZ',
            defaultMessage: 'This offer has sold out',
            description: 'Message displayed when cruise offer has no available departures',
          })}
        </BodyText>
      </Group>}
    </>
  )
}

export default CruisePriceRoomTypeInfo
